import React, { useState, useEffect, useMemo, useCallback } from 'react';
import './Parichay.css';
import { apiCall } from '../utils/api';

const INTENT_OPTIONS = [
  { value: 'career_advice', label: 'Career Advice', icon: '💡' },
  { value: 'industry_insights', label: 'Industry Insights', icon: '📊' },
  { value: 'mentorship', label: 'Mentorship', icon: '🎯' },
  { value: 'job_referral', label: 'Job Referral', icon: '💼' },
  { value: 'other', label: 'Other', icon: '✨' }
];

const STORAGE_KEY = 'parichay_introductions';

// Helper to get initials
const getInitials = (name) => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const Parichay = () => {
  const [activeTab, setActiveTab] = useState('people'); // 'people' | 'connections'
  const [searchQuery, setSearchQuery] = useState('');
  const [introductions, setIntroductions] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [selectedIntent, setSelectedIntent] = useState('');
  const [noteText, setNoteText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [professionals, setProfessionals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [userQualification, setUserQualification] = useState(null);

  // Fetch user details to get qualification
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response = await apiCall.get('/users/get/user/details');
        const userData = response.data || response;
        if (userData.qualification) {
          setUserQualification(userData.qualification);
        }
      } catch (e) {
        console.error('Failed to fetch user details:', e);
      }
    };
    fetchUserDetails();
  }, []);

  // Fetch profiles - first matching qualification, then others
  useEffect(() => {
    const fetchProfiles = async () => {
      setIsLoading(true);
      try {
        let matchingProfiles = [];
        let otherProfiles = [];

        // First fetch profiles matching user's qualification
        if (userQualification) {
          try {
            const matchingResponse = await apiCall.post('/users/profiles/search', {
              filters: { qualification: userQualification }
            });
            matchingProfiles = matchingResponse.data || matchingResponse || [];
          } catch (e) {
            console.error('Failed to fetch matching profiles:', e);
          }
        }

        // Then fetch all profiles (we'll filter out duplicates)
        try {
          const allResponse = await apiCall.post('/users/profiles/search', {
            filters: {}
          });
          const allProfiles = allResponse.data || allResponse || [];
          
          // Filter out profiles that are already in matchingProfiles
          const matchingIds = new Set(matchingProfiles.map(p => p.id || p._id));
          otherProfiles = allProfiles.filter(p => !matchingIds.has(p.id || p._id));
        } catch (e) {
          console.error('Failed to fetch all profiles:', e);
        }

        // Combine: matching profiles first, then others, reversed order
        const combined = [...matchingProfiles, ...otherProfiles];
        setProfessionals(combined.reverse());
      } catch (e) {
        console.error('Failed to fetch profiles:', e);
        setProfessionals([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfiles();
  }, [userQualification]);

  // Default connection requests (pre-populated)
  const DEFAULT_CONNECTIONS = {
    '6960c33cb1bcd635f2818139': {
      status: 'pending',
      intent: 'career_advice',
      message: '',
      date: new Date().toISOString()
    },
    '6960c314b1bcd635f2818137': {
      status: 'pending',
      intent: 'career_advice',
      message: '',
      date: new Date().toISOString()
    }
  };

  // Load introductions from localStorage on mount, merge with defaults
  useEffect(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      const storedIntros = stored ? JSON.parse(stored) : {};
      // Merge defaults with stored (stored takes priority for existing keys)
      setIntroductions({ ...DEFAULT_CONNECTIONS, ...storedIntros });
    } catch (e) {
      console.error('Failed to load introductions:', e);
      setIntroductions(DEFAULT_CONNECTIONS);
    }
  }, []);

  // Save introductions to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(introductions));
    } catch (e) {
      console.error('Failed to save introductions:', e);
    }
  }, [introductions]);

  const matchesSearch = useCallback((p) => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    const onboarding = p.onboarding_choices || {};
    return (
      (p.name || onboarding.name || '').toLowerCase().includes(query) ||
      (onboarding.qualification || p.qualification || '').toLowerCase().includes(query) ||
      (onboarding.city || p.city || '').toLowerCase().includes(query) ||
      (onboarding.current_role || p.current_role || '').toLowerCase().includes(query)
    );
  }, [searchQuery]);

  // People tab list (searchable)
  const peopleResults = useMemo(() => {
    return professionals.filter(matchesSearch);
  }, [professionals, matchesSearch]);

  // Helper to get consistent ID from professional object
  const getProfessionalId = useCallback((p) => {
    return p.id || p._id || p.user_id;
  }, []);

  // Connections tab list (people you already requested/connected with)
  const connectionsResults = useMemo(() => {
    return professionals
      .filter(p => introductions[getProfessionalId(p)])
      .filter(matchesSearch);
  }, [professionals, introductions, matchesSearch, getProfessionalId]);

  const getConnectionStatus = useCallback((professional) => {
    const id = getProfessionalId(professional);
    const intro = introductions[id];
    if (!intro) return 'none';
    return intro.status;
  }, [introductions, getProfessionalId]);

  const handleSendNote = (professional) => {
    setSelectedProfessional(professional);
    setSelectedIntent('');
    setNoteText('');
    setShowModal(true);
  };

  const handleSubmitRequest = async () => {
    if (!selectedIntent) return;
    
    setIsSending(true);
    
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Save the introduction request
    const professionalId = getProfessionalId(selectedProfessional);
    setIntroductions(prev => ({
      ...prev,
      [professionalId]: {
        status: 'pending',
        intent: selectedIntent,
        message: noteText,
        date: new Date().toISOString()
      }
    }));
    
    setIsSending(false);
    setShowModal(false);
    setSelectedProfessional(null);
    setSelectedIntent('');
    setNoteText('');
  };

  const handleCloseModal = () => {
    if (!isSending) {
      setShowModal(false);
      setSelectedProfessional(null);
      setSelectedIntent('');
      setNoteText('');
    }
  };

  const renderProfessionalCard = (professional) => {
    const status = getConnectionStatus(professional);
    const id = professional.id || professional._id || professional.user_id;
    const onboarding = professional.onboarding_choices || {};
    
    // Get name from top level or onboarding_choices
    const name = professional.name || onboarding.name || 'Unknown';
    
    const profileUrl =
      onboarding.profile_url ||
      onboarding.profileUrl ||
      professional.profile_url ||
      professional.profileUrl ||
      professional.avatar ||
      professional.profilePhoto ||
      '';
    
    // Get qualification from onboarding_choices or top level
    const qualification = onboarding.qualification || 
                          professional.qualification || 
                          professional.profession || 
                          '';
    
    // Get city from onboarding_choices or top level
    const city = onboarding.city || 
                 professional.city || 
                 professional.location || 
                 '';
    
    // Get experience from onboarding_choices or top level
    const experience = onboarding.total_experience_years || 
                       professional.total_experience_years || 
                       professional.experience || 
                       0;
    
    // Build info line: "CA • Jaipur • 2 yrs exp"
    const infoItems = [
      qualification,
      city,
      experience ? `${experience} yrs exp` : null
    ].filter(Boolean);
    const infoLine = infoItems.join(' • ');
    
    return (
      <div
        key={id}
        className="parichay-person-card"
      >
        <div className="parichay-person-row">
          <div
            className={`parichay-person-avatar${profileUrl ? ' has-photo' : ''}`}
            aria-hidden="true"
            style={profileUrl ? { backgroundImage: `url(${profileUrl})` } : undefined}
          >
            {!profileUrl && getInitials(name)}
          </div>
          <div className="parichay-person-main">
            <div className="parichay-person-name">{name}</div>
            {infoLine && <div className="parichay-person-info">{infoLine}</div>}
          </div>
          <div className="parichay-person-action">
            {status === 'none' ? (
              <button
                className="parichay-btn parichay-btn-primary"
                onClick={() => handleSendNote(professional)}
              >
                Connect
              </button>
            ) : (
              <button className="parichay-btn parichay-btn-disabled" disabled>
                {status === 'pending' ? 'Requested' : 'Connected'}
              </button>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="parichay-screen">
      <div className="parichay-topbar">
        <div className="parichay-title">Parichay</div>
        <div className="parichay-subtitle">Connect with people in your field</div>

        <div className="parichay-search">
          <input
            type="text"
            placeholder="Search by name, role, or city"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="parichay-search-input"
          />
          {searchQuery && (
            <button
              className="parichay-search-clear"
              onClick={() => setSearchQuery('')}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>

        <div className="parichay-tabs" role="tablist" aria-label="Parichay tabs">
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'people'}
            className={`parichay-tab ${activeTab === 'people' ? 'active' : ''}`}
            onClick={() => setActiveTab('people')}
          >
            People
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={activeTab === 'connections'}
            className={`parichay-tab ${activeTab === 'connections' ? 'active' : ''}`}
            onClick={() => setActiveTab('connections')}
          >
            Connections
          </button>
        </div>
      </div>

      <div className="parichay-scroll">
        <div className="parichay-list">
          {isLoading ? (
            <div className="parichay-empty">
              <div className="parichay-spinner"></div>
              <p>Loading profiles...</p>
            </div>
          ) : activeTab === 'people' ? (
            <>
              {peopleResults.map(p => renderProfessionalCard(p))}
              {peopleResults.length === 0 && (
                <div className="parichay-empty">
                  <h3>No results found</h3>
                  <p>Try a different search.</p>
                </div>
              )}
            </>
          ) : (
            <>
              {connectionsResults.map(p => renderProfessionalCard(p))}
              {connectionsResults.length === 0 && (
                <div className="parichay-empty">
                  <h3>No connections yet</h3>
                  <p>Send a request from the People tab to see it here.</p>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Bottom Sheet Modal */}
      {showModal && (
        <div className="parichay-modal-overlay" onClick={handleCloseModal}>
          <div className="parichay-modal" onClick={e => e.stopPropagation()}>
            <div className="parichay-modal-header">
              <div className="parichay-modal-handle"></div>
              <h2>Send a note to {selectedProfessional?.name.split(' ')[0]}</h2>
              <button className="parichay-modal-close" onClick={handleCloseModal}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>

            <div className="parichay-modal-body">
              <div className="parichay-modal-section">
                <label className="parichay-modal-label">
                  What would you like to connect about? <span className="required">*</span>
                </label>
                <div className="parichay-intent-options">
                  {INTENT_OPTIONS.map(intent => (
                    <button
                      key={intent.value}
                      className={`parichay-intent-btn ${selectedIntent === intent.value ? 'selected' : ''}`}
                      onClick={() => setSelectedIntent(intent.value)}
                    >
                      <span className="parichay-intent-icon">{intent.icon}</span>
                      <span>{intent.label}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="parichay-modal-section">
                <label className="parichay-modal-label">
                  Add a personal note <span className="optional">(optional)</span>
                </label>
                <textarea
                  className="parichay-modal-textarea"
                  placeholder="Introduce yourself and share why you'd like to connect..."
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  rows={4}
                />
              </div>
            </div>

            <div className="parichay-modal-footer">
              <button 
                className="parichay-btn parichay-btn-ghost"
                onClick={handleCloseModal}
                disabled={isSending}
              >
                Cancel
              </button>
              <button 
                className="parichay-btn parichay-btn-primary"
                onClick={handleSubmitRequest}
                disabled={!selectedIntent || isSending}
              >
                {isSending ? (
                  <>
                    <span className="parichay-spinner"></span>
                    Sending…
                  </>
                ) : (
                  <>
                    Send Request
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Parichay;

